'use client';

import { FC, useCallback, useEffect, useState, useContext } from 'react';
import styles from './DocumentChecklistBlock.module.css';
import { ClientQuestionnaireContext } from '@/_contexts/client/ClientQuestionnaireProvider';
import { DocumentChecklistBlockProps } from './DocumentChecklistBlock.types';
import {
  Alert,
  DocumentChecklist,
  Flex,
  Button,
  ButtonGroup,
  Heading,
  LoadingIndeterminate,
  Separator,
  UploadBox,
  Text,
} from '@/_components';
import { PreviewContext } from '@/_contexts/PreviewProvider';
import { ToastContext } from '@/_contexts/ToastProvider';
import { UserContext } from '@/_contexts/UserProvider';
import { Questionnaire } from '@/_types/models/questionnaire';
import { getClasses } from '@/_utils/classnames';
import { handleAcceptedUploadedFiles } from '@/_utils/files';
import { getUploadedItems } from '@/_utils/answers';
import {
  getAllDocumentChecklistItems,
  getUploadPath,
} from '@/_utils/questionnaire';

export const DocumentChecklistBlock: FC<DocumentChecklistBlockProps> = ({
  title,
  isReviewSection,
  isLocked,
  mode,
  questionnaire: passedQuestionnaire,
  section,
}) => {
  const [isOpen, setOpen] = useState<boolean>(true);
  const [isProcessingDocuments, setIsProcessingDocuments] =
    useState<boolean>(false);
  const [questionnaire, setQuestionnaire] = useState<any>(passedQuestionnaire);
  const [isUploading, setIsUploading] = useState<boolean>(false);

  const { addToast, resetToasts } = useContext(ToastContext);
  const { token } = useContext(UserContext);

  const isAdminPreview: boolean = mode !== 'client';

  const {
    loadSelectedQuestionnaireWithAllFields,
    selectedQuestionnaire,
    selectedQuestionnaireWithAllFields,
  } = useContext(ClientQuestionnaireContext);

  const { loadPreviewQuestionnaire, previewQuestionnaire } =
    useContext(PreviewContext);

  useEffect(
    () => {
      if (
        !isAdminPreview &&
        selectedQuestionnaire &&
        !selectedQuestionnaireWithAllFields
      ) {
        loadSelectedQuestionnaireWithAllFields();
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      isAdminPreview,
      selectedQuestionnaire?.id,
      selectedQuestionnaireWithAllFields?.id,
    ],
  );

  useEffect(() => {
    if (selectedQuestionnaireWithAllFields) {
      setQuestionnaire(selectedQuestionnaireWithAllFields);
    } else if (previewQuestionnaire) {
      setQuestionnaire(previewQuestionnaire);
    }
  }, [previewQuestionnaire, selectedQuestionnaireWithAllFields]); // eslint-disable-line react-hooks/exhaustive-deps

  const onToggleOpen = () => setOpen(!isOpen);

  const documentChecklistBlockClasses: string = getClasses(
    styles.documentChecklistBlock,
    {
      [styles.documentChecklistBlockPadding]: !isReviewSection,
    },
  );

  const uploadPath: string = getUploadPath(questionnaire);

  const pollForDocumentsInQuestionnaire = async (
    uploadedFiles: Array<File>,
  ) => {
    let timesUploadedFileHasNotBeenFound = 0;

    const documentStillProcessing = (questionnaire?: Questionnaire | void) => {
      if (
        questionnaire?.extracted_documents?.length === 0 &&
        uploadedFiles.length > 0
      ) {
        return true;
      }

      const names =
        questionnaire?.extracted_documents?.map((ed: any) =>
          ed.source.split('/').pop(),
        ) || [];

      if (!uploadedFiles.some((f: any) => names.includes(f.name))) {
        // Probably means something went wrong but it's tough to say and should never happen. However, I don't want this to poll forever if I'm wrong and it happens.
        if (timesUploadedFileHasNotBeenFound > 3) {
          return false;
        }

        timesUploadedFileHasNotBeenFound++;
        return true;
      }

      return questionnaire?.extracted_documents?.some(
        (d: any) => d.status === 'processing',
      );
    };

    let count = 0;
    let loaded: Questionnaire | undefined | void;

    const loadQuestionnaireWithWait = async () => {
      await new Promise((resolve) => setTimeout(resolve, 750));

      loaded = isAdminPreview
        ? await loadPreviewQuestionnaire()
        : await loadSelectedQuestionnaireWithAllFields();
    };

    // We want to initially load the questionnaire and then poll for the documents.
    await loadQuestionnaireWithWait();

    do {
      await loadQuestionnaireWithWait();

      count++;

      if (count > 200) {
        break;
      }
    } while (documentStillProcessing(loaded));

    resetToasts();
    return Promise.resolve();
  };

  const onDrop = useCallback(
    async (acceptedFiles: Array<File>) => {
      if (!token || !questionnaire) return;

      try {
        setIsUploading(true);
        setIsProcessingDocuments(true);

        await handleAcceptedUploadedFiles({
          acceptedFiles,
          addToast,
          companyId: questionnaire.company_id!,
          description: '',
          documentType: 'bulk_document',
          questionnaireId: questionnaire?.id!,
          requiresSplitting: false,
          selectedOptions: ['Document Checklist'],
          token,
          uploadPath,
        });
      } catch (err) {
        console.error(err);
      } finally {
        setIsUploading(false);
        await pollForDocumentsInQuestionnaire(acceptedFiles);
        setIsProcessingDocuments(false);
      }
    },
    [addToast, questionnaire, token, uploadPath], // eslint-disable-line react-hooks/exhaustive-deps
  );

  useEffect(() => {
    const updatedDocuments = allDocumentsNeededAsQuestions.reduce(
      (acc: any, question: any) => {
        let documents = null;

        if (question.answer) {
          documents = getUploadedItems(question.answer);
        }

        if (
          !acc.some((hash: any) => Object.keys(hash)[0] === String(question.id))
        ) {
          acc.push({ [question.id]: documents || [] });
        }

        return acc;
      },
      [],
    );

    setDocumentsFromAnswers(updatedDocuments);
  }, [questionnaire]); // eslint-disable-line react-hooks/exhaustive-deps

  const allDocumentsNeededAsQuestions =
    (questionnaire && getAllDocumentChecklistItems(questionnaire)) || [];

  const [documentsFromAnswers, setDocumentsFromAnswers] = useState<
    Array<Record<number, Array<string>>>
  >([]);
  const [didAtLeastOneDrag, setDidAtLeastOneDrag] = useState(false);

  const loadQuestionnaire = isAdminPreview
    ? loadPreviewQuestionnaire
    : loadSelectedQuestionnaireWithAllFields;

  useEffect(() => {
    return () => {
      if (didAtLeastOneDrag) {
        loadQuestionnaire();
        setDidAtLeastOneDrag(false);
      }
    };
  }, [didAtLeastOneDrag, loadQuestionnaire, setDidAtLeastOneDrag]);

  return (
    <Flex
      className={documentChecklistBlockClasses}
      isColumn={true}
      padding={10}
      gap={6}
    >
      {!isReviewSection && (
        <Flex isBetween={true} isYCentered={true} gap={4}>
          <Heading level={4}>{title}</Heading>
          <ButtonGroup>
            <Button
              value="Open"
              intent="ghost"
              iconLeft={isOpen ? 'caret-down' : 'caret-up'}
              onClick={onToggleOpen}
              isIconOnly={true}
              size="small"
            />
          </ButtonGroup>
        </Flex>
      )}

      {(isOpen || isReviewSection) && (
        <Flex isColumn={true} gap={6}>
          <Flex isColumn={true} gap={2}>
            <Alert type="info" icon="info">
              {section?.instruction ||
                'You may upload any of the requested documents here and they will be automatically categorized into their respective questions.'}
            </Alert>

            <UploadBox
              allowMultiple={true}
              isDisabled={
                isUploading ||
                !questionnaire ||
                isLocked ||
                mode === 'admin-template'
              }
              isLoading={isUploading}
              isOnSubtleBackground={true}
              onDrop={onDrop}
            />
          </Flex>
          <Separator />
          {(isProcessingDocuments || !questionnaire) && (
            <>
              <LoadingIndeterminate />
              <Text fontWeight="bold" textAlign="center">
                Your documents are processing, you can continue uploading other
                documents.
              </Text>
            </>
          )}
          {questionnaire && (
            <DocumentChecklist
              documentsFromAnswers={documentsFromAnswers}
              mode={mode}
              loadQuestionnaire={loadQuestionnaire}
              questionnaire={questionnaire!}
              setDidAtLeastOneDrag={setDidAtLeastOneDrag}
              setDocumentsFromAnswers={setDocumentsFromAnswers}
            />
          )}
        </Flex>
      )}
    </Flex>
  );
};
